import { Button, formatDate, Skeleton, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { changePackSchedule, schedulePack, type PackScheduleInfo, type PatternBody, type PatternPreset, type PatternPreview, type Resolution } from "../../api/pt";
import { useServices } from "../../app/services";
import { Field, FormError, messageOf, styles as c, useScope } from "./common";
import k from "./classes.module.css";
import { fromMinutes, toMinutes, WEEKDAY_NAMES } from "./time";

const PRESETS: Array<{ value: PatternPreset; label: string; days: number[] }> = [
  { value: "mwf", label: "Mon / Wed / Fri", days: [1, 3, 5] },
  { value: "tts", label: "Tue / Thu / Sat", days: [2, 4, 6] },
  { value: "mon_sat", label: "Mon to Sat", days: [1, 2, 3, 4, 5, 6] },
  { value: "custom", label: "Pick days", days: [] },
];

const REASON: Record<string, string> = {
  trainer_unavailable: "Trainer is off at that time",
  trainer_double_booked: "Trainer has another session",
  member_double_booked: "Member has another session",
  pack_frozen: "Pack is frozen",
  pack_inactive: "Outside the pack's dates",
  pack_exhausted: "No sessions left",
  in_the_past: "Already past",
};

interface Fix { skip: boolean; trainerId: string; start: string }

/**
 * One pattern, once: weekdays, start date, a time slot and a trainer. "Check the slots" asks the API what it would book without
 * writing anything; dates it cannot book come back with the reason, and staff pick another trainer or time for each (or leave
 * it out) before the pack is booked. `change` moves only the sessions that have not happened yet.
 */
export function PatternForm({ mode, planId, info, trainers, nameOf, date, trainerId: initialTrainer, initial, onDone }: {
  mode: "create" | "change"; planId: string; info: PackScheduleInfo; trainers: string[]; nameOf: (id: string | null) => string; date: string;
  trainerId: string; initial?: { preset: PatternPreset; weekdays: number[]; start: string; end: string }; onDone: () => void;
}) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const daily = info.bookingStyle === "daily";
  const [preset, setPreset] = useState<PatternPreset>(daily ? "mon_sat" : initial?.preset ?? "mwf");
  const [custom, setCustom] = useState<number[]>(initial?.preset === "custom" ? initial.weekdays : [1, 3, 5]);
  const [startDate, setStartDate] = useState(date < info.startDate ? info.startDate : date);
  const [start, setStart] = useState(initial?.start ?? "07:00");
  const [end, setEnd] = useState(initial?.end ?? "08:00");
  const [trainerId, setTrainerId] = useState(initialTrainer);
  const [fixes, setFixes] = useState<Record<string, Fix>>({});
  const [preview, setPreview] = useState<PatternPreview | null>(null);
  const [partial, setPartial] = useState(false);
  /** Fixes were edited after the last check: the list on screen is no longer what the API would do. */
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const HHMM = /^\d\d:\d\d$/;
  // While a time is half typed (cleared, or one digit in) keep the last sensible length instead of NaN.
  const len = HHMM.test(start) && HHMM.test(end) && end > start ? toMinutes(end) - toMinutes(start) : 60;

  const resolutions = (): Resolution[] =>
    Object.entries(fixes).flatMap(([d, f]) => {
      if (f.skip) return [{ date: d, skip: true }];
      const r: Resolution = { date: d };
      if (f.trainerId && f.trainerId !== trainerId) r.trainerId = f.trainerId;
      if (f.start && f.start !== start) { r.start = f.start; r.end = fromMinutes(Math.min(1439, toMinutes(f.start) + len)); }
      return r.trainerId || r.start ? [r] : [];
    });
  const body = (extra: Partial<PatternBody>): PatternBody => ({
    preset, ...(preset === "custom" ? { weekdays: custom } : {}), start, end, trainerId, resolutions: resolutions(), ...extra,
  });
  const call = (b: PatternBody) => (mode === "create" ? schedulePack(client, planId, { ...b, startDate }) : changePackSchedule(client, planId, b));

  const check = useMutation({
    mutationFn: () => call(body({ dryRun: true })),
    onSuccess: (r) => { setPreview(r); setStale(false); setError(null); },
    onError: (e) => { setPreview(null); setError(messageOf(e)); },
  });
  const confirm = useMutation({
    mutationFn: () => call(body(partial ? { allowPartial: true } : {})),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["pt"] });
      void qc.invalidateQueries({ queryKey: ["classes"] });
      toast.show(mode === "create" ? `${r.sessions?.length ?? r.dates.length} sessions booked` : `${r.dates.length} sessions moved`);
      onDone();
    },
    onError: (e) => setError(messageOf(e)),
  });
  const reset = () => { setPreview(null); setStale(false); setError(null); };
  const submitCheck = () => {
    if (preset === "custom" && custom.length === 0) return setError("Pick at least one day.");
    if (!trainerId) return setError("Choose a trainer.");
    if (end <= start) return setError("The end time is before the start time.");
    setError(null);
    check.mutate();
  };
  const fix = (d: string, patch: Partial<Fix>) => { setFixes((f) => ({ ...f, [d]: { skip: false, trainerId, start, ...f[d], ...patch } })); setStale(true); };
  const open = preview?.conflicts ?? [];
  const bookable = preview !== null && !stale && preview.dates.length > 0 && (open.length === 0 || partial);
  const label = mode === "create" ? "Book" : "Move";

  return (
    <form className={c.stack} aria-label={mode === "create" ? "Weekly pattern" : "Change the slot"} onSubmit={(e) => { e.preventDefault(); if (bookable) confirm.mutate(); else submitCheck(); }}>
      {mode === "create" ? <p className={c.note}>{info.bookingStyle === "daily" ? "This pack runs Monday to Saturday." : "This pack is booked on a weekly pattern."} Pick it once; every session in the pack is booked at the same time.</p>
        : <p className={c.note}>Only sessions that have not happened yet move. Past and delivered sessions stay as they are.</p>}
      <fieldset className={`${c.stack} ${k.plain}`} disabled={daily}>
        <legend className={c.note}>Days</legend>
        <div className={k.seg7} role="radiogroup" aria-label="Pattern">
          {PRESETS.map((p) => (
            <label key={p.value} className={c.plan}><input type="radio" name="pattern" value={p.value} checked={preset === p.value} onChange={() => { setPreset(p.value); reset(); }} /><span>{p.label}</span></label>
          ))}
        </div>
        {preset === "custom" ? (
          <div className={k.seg7} role="group" aria-label="Weekdays">
            {[1, 2, 3, 4, 5, 6, 0].map((d) => (
              <label key={d} className={c.check}><input type="checkbox" checked={custom.includes(d)} onChange={(e) => { setCustom((cur) => (e.target.checked ? [...cur, d] : cur.filter((x) => x !== d))); reset(); }} />{WEEKDAY_NAMES[d]}</label>
            ))}
          </div>
        ) : null}
      </fieldset>
      {mode === "create" ? <Field label="First session on or after" htmlFor="pat-date" hint={`Pack runs ${formatDate(info.startDate, sc.ctx.locale, "short")} to ${formatDate(info.endDate, sc.ctx.locale, "short")}.`}><input id="pat-date" type="date" className={c.input} value={startDate} min={info.startDate} onChange={(e) => { setStartDate(e.target.value); reset(); }} /></Field> : null}
      <div className={c.row}>
        <Field label="Starts" htmlFor="pat-start"><input id="pat-start" type="time" className={c.input} value={start} onChange={(e) => { setStart(e.target.value); if (HHMM.test(e.target.value)) setEnd(fromMinutes(Math.min(1439, toMinutes(e.target.value) + len))); reset(); }} /></Field>
        <Field label="Ends" htmlFor="pat-end"><input id="pat-end" type="time" className={c.input} value={end} onChange={(e) => { setEnd(e.target.value); reset(); }} /></Field>
      </div>
      <Field label="Trainer" htmlFor="pat-trainer"><select id="pat-trainer" className={c.input} value={trainerId} onChange={(e) => { setTrainerId(e.target.value); reset(); }}>{trainers.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}</select></Field>
      <FormError message={error} />

      {check.isPending ? <div aria-busy="true" aria-label="Checking the slots"><Skeleton width="60%" height={14} /><Skeleton width="90%" height={40} /></div> : null}
      {preview && !check.isPending ? (
        <section className={c.stack} aria-label="Slot check">
          {preview.dates.length === 0 && open.length === 0 ? <p className={c.note}>No session falls inside the pack for that pattern. Try other days or an earlier start.</p> : (
            <p><strong>{preview.dates.length} of {preview.plan.target} sessions</strong> fit
              {preview.dates.length > 0 ? `, ${formatDate(preview.dates[0]!.date, sc.ctx.locale, "short")} to ${formatDate(preview.dates[preview.dates.length - 1]!.date, sc.ctx.locale, "short")}` : ""}.</p>
          )}
          {preview.plan.holidaySkipped.length > 0 ? (
            <p className={c.note}>
              Gym closed on {preview.plan.holidaySkipped.map((d) => formatDate(d, sc.ctx.locale, "short")).join(", ")}, so {preview.plan.holidaySkipped.length === 1 ? "that day is" : "those days are"} skipped.
              {preview.plan.extension.length > 0 ? ` ${preview.plan.extension.length === 1 ? "It is" : "They are"} added after the pack's end, which moves to ${formatDate(preview.newEnd, sc.ctx.locale, "short")}.` : ""}
            </p>
          ) : null}
          {open.length > 0 ? (
            <div className={c.stack} role="group" aria-label="Dates that need another slot">
              <p className={c.err} role="alert">{open.length} {open.length === 1 ? "date needs" : "dates need"} another slot or trainer.</p>
              {open.map((cf) => {
                const f = fixes[cf.date] ?? { skip: false, trainerId, start };
                return (
                  <div key={cf.date} className={k.bk} role="group" aria-label={`Fix ${cf.date}`}>
                    <div><strong>{formatDate(cf.date, sc.ctx.locale, "weekday")} {formatDate(cf.date, sc.ctx.locale, "short")}</strong><span className={c.sub}>{REASON[cf.reason] ?? cf.reason}</span></div>
                    <div className={k.slotRow}>
                      <Field label="Trainer" htmlFor={`fx-t-${cf.date}`}><select id={`fx-t-${cf.date}`} className={c.input} value={f.trainerId} disabled={f.skip} onChange={(e) => fix(cf.date, { trainerId: e.target.value })}>{trainers.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}</select></Field>
                      <Field label="Starts" htmlFor={`fx-s-${cf.date}`}><input id={`fx-s-${cf.date}`} type="time" className={c.input} value={f.start} disabled={f.skip} onChange={(e) => fix(cf.date, { start: e.target.value })} /></Field>
                    </div>
                    <label className={c.check}><input type="checkbox" checked={f.skip} onChange={(e) => fix(cf.date, { skip: e.target.checked })} />Leave this date out</label>
                  </div>
                );
              })}
              <label className={c.check}><input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />Book the rest and leave these {open.length} out</label>
            </div>
          ) : null}
        </section>
      ) : null}
      <div className={c.row}>
        {bookable
          ? <Button variant="primary" type="submit" busy={confirm.isPending} busyLabel="Booking…">{`${label} ${preview!.dates.length} sessions`}</Button>
          : <Button variant="primary" type="submit" busy={check.isPending} busyLabel="Checking…">{preview && (open.length > 0 || stale) ? "Check again" : "Check the slots"}</Button>}
        <Button variant="plain" type="button" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}
